import React, { useId, useMemo } from "react";
import { useInstagramApiPublishingOn } from "../../../../../utils/feature-toggle-utils";

import { FIELDS } from "../../../../../constants/PostFormFields";
import {
  hasApiCapableInstagramAccount,
  type TagCapabilityAccount,
} from "../../PostTags/TagCapabilityWarning";
import { isInstagramAccount } from "../../../../../entities/accounts/libs/account-platform";
import type { AccountPlatformType } from "../../../../../shared/types/accounts";
import { SwitchFieldRow } from "../SwitchFieldRow/SwitchFieldRow";
import { INSTAGRAM_ONLY } from "../PlatformLabelBadges/PlatformLabelBadges";

interface InstagramAiDisclosureProps {
  selectedPlatform: AccountPlatformType | "LINKED";
  formAccounts: TagCapabilityAccount[];
  updateField: (field: string, value: boolean) => void;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost?: any;
}

/**
 * SKE-4544. Discloses to Instagram that this post was made with AI.
 *
 * Meta reads `is_ai_generated` back on a published post, so unlike most of the
 * publish options this one can be checked against Instagram itself.
 *
 * Gated on ANY selected Instagram account being able to publish through the API,
 * reusing `hasApiCapableInstagramAccount` rather than adding a second capability
 * rule. The phone path carries no equivalent field, so an account still on it
 * publishes the post without the disclosure.
 */
export const InstagramAiDisclosure: React.FC<InstagramAiDisclosureProps> = ({
  selectedPlatform,
  formAccounts,
  updateField,
  disabled,
  platformPost,
}) => {
  const apiPublishingOn = useInstagramApiPublishingOn();
  const fieldId = useId();

  const show = useMemo(() => {
    if (selectedPlatform !== "IG" && selectedPlatform !== "LINKED")
      return false;
    const igAccounts = (formAccounts ?? []).filter((account) =>
      isInstagramAccount(account as never)
    );
    if (igAccounts.length === 0) return false;
    return hasApiCapableInstagramAccount(igAccounts, apiPublishingOn);
  }, [selectedPlatform, formAccounts, apiPublishingOn]);

  if (!show) return null;

  const checked = platformPost?.instagram?.isAiGenerated === true;

  return (
    <SwitchFieldRow
      data-testid="ig-ai-disclosure"
      id={fieldId}
      label="Made with AI"
      helper="Tells Instagram this post was created with AI."
      checked={checked}
      disabled={disabled}
      selectedPlatform={selectedPlatform}
      platforms={INSTAGRAM_ONLY}
      onCheckedChange={(next: boolean) =>
        updateField(FIELDS.IG_IS_AI_GENERATED, next)
      }
    />
  );
};

export default InstagramAiDisclosure;
